import React, { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { formatDate, formatDateTime } from "@/lib/dates";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/use-toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { ChevronDown, ChevronRight, Plus, Tag as TagIcon, X } from "lucide-react";

type BonusType = "PERCENT" | "PER_BASE_AMOUNT" | "FIXED_AMOUNT";

type Promo = {
  id: string;
  promo_name: string;
  start_date: string; // YYYY-MM-DD
  end_date: string; // YYYY-MM-DD
  is_active: boolean | string; // Handle both boolean and string from DB

  min_qualifying_amount: number; // default 25
  below_min_bonus_rate: number; // default 0.5
  max_bonus_base: number; // default 150

  bonus_type: BonusType;
  percent_rate: number | null; // e.g. 0.40
  base_amount: number | null; // e.g. 25
  bonus_per_base: number | null; // e.g. 5
  fixed_bonus_amount: number | null; // e.g. 10 flat

  promo_notes: string | null;

  created_at: string;
  updated_at: string;
};

type EcardTag = {
  id: string;
  name: string;
  description: string | null;
  color: string;
  applies_to_card: boolean;
  applies_to_promotion: boolean;
  is_active: boolean;
};

type EcardPromoDefaults = {
  minQualifyingAmount: string;
  belowMinBonusRate: string;
  maxBonusBase: string;
  bonusType: BonusType;
  percentRate: string;
  baseAmount: string;
  bonusPerBase: string;
  fixedBonusAmount: string;
  defaultTagColor: string;
};

const DEFAULT_ECARD_PROMO_SETTINGS: EcardPromoDefaults = {
  minQualifyingAmount: "25",
  belowMinBonusRate: "0.50",
  maxBonusBase: "150",
  bonusType: "PERCENT",
  percentRate: "1.00",
  baseAmount: "25",
  bonusPerBase: "5",
  fixedBonusAmount: "10",
  defaultTagColor: "#3b82f6",
};

const ECARD_PROMO_SETTING_NAMES = [
  "ecard_promo_min_qualifying_amount",
  "ecard_promo_below_min_bonus_rate",
  "ecard_promo_max_bonus_base",
  "ecard_promo_bonus_type",
  "ecard_promo_percent_rate",
  "ecard_promo_base_amount",
  "ecard_promo_bonus_per_base",
  "ecard_promo_fixed_bonus_amount",
  "ecard_promo_default_tag_color",
];

function normalizeBonusType(value: string | null | undefined): BonusType {
  if (value === "PER_BASE_AMOUNT" || value === "FIXED_AMOUNT" || value === "PERCENT") {
    return value;
  }
  return "PERCENT";
}


const COLOR_PRESETS = [
  "#3b82f6", "#10b981", "#f59e0b", "#ef4444",
  "#8b5cf6", "#ec4899", "#06b6d4", "#84cc16",
  "#f97316", "#6366f1",
];

// Helper to normalize is_active from DB (could be boolean or string)
function isPromoActive(val: boolean | string | null | undefined): boolean {
  if (typeof val === "boolean") return val;
  if (typeof val === "string") {
    return val.toLowerCase() === "true" || val === "1";
  }
  return false;
}

const isoDate = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const fmtMoney = (n: number | null) => {
  if (n === null || n === undefined) return "-";
  return new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD" }).format(n);
};

const fmtRate = (n: number | null) => {
  if (n === null || n === undefined) return "-";
  return n.toFixed(4);
};

const fmtPercent = (n: number | null) => {
  if (n === null || n === undefined) return "-";
  return `${(n * 100).toFixed(0)}%`;
};

function formatPromoSummary(p: Promo) {
  if (p.bonus_type === "PERCENT") {
    const pct = ((p.percent_rate ?? 0) * 100).toFixed(0);
    return `${pct}% bonus`;
  }
  if (p.bonus_type === "FIXED_AMOUNT") {
    return `$${(p.fixed_bonus_amount ?? 0).toFixed(2)} flat bonus`;
  }
  const base = p.base_amount ?? 25;
  const per = p.bonus_per_base ?? 0;
  return `$${per} per $${base}`;
}

export default function AdminECardPromosPage() {
  const [promos, setPromos] = useState<Promo[]>([]);
  const [allTags, setAllTags] = useState<EcardTag[]>([]);
  const [promoTagMap, setPromoTagMap] = useState<Record<string, string[]>>({}); // promo_id -> tag_ids
  const [loading, setLoading] = useState(false);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [promoDefaults, setPromoDefaults] = useState<EcardPromoDefaults>(DEFAULT_ECARD_PROMO_SETTINGS);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null); // null = new promo

  // Create/edit fields
  const [promoName, setPromoName] = useState("");
  const [startDate, setStartDate] = useState<string>(isoDate(new Date()));
  const [endDate, setEndDate] = useState<string>(isoDate(new Date()));
  const [isActive, setIsActive] = useState<boolean>(true);

  const [minQual, setMinQual] = useState<string>(DEFAULT_ECARD_PROMO_SETTINGS.minQualifyingAmount);
  const [belowMinRate, setBelowMinRate] = useState<string>(DEFAULT_ECARD_PROMO_SETTINGS.belowMinBonusRate);
  const [maxBonusBase, setMaxBonusBase] = useState<string>(DEFAULT_ECARD_PROMO_SETTINGS.maxBonusBase);

  const [bonusType, setBonusType] = useState<BonusType>(DEFAULT_ECARD_PROMO_SETTINGS.bonusType);
  const [percentRate, setPercentRate] = useState<string>(DEFAULT_ECARD_PROMO_SETTINGS.percentRate);

  const [baseAmount, setBaseAmount] = useState<string>(DEFAULT_ECARD_PROMO_SETTINGS.baseAmount);
  const [bonusPerBase, setBonusPerBase] = useState<string>(DEFAULT_ECARD_PROMO_SETTINGS.bonusPerBase);

  const [fixedBonusAmount, setFixedBonusAmount] = useState<string>(DEFAULT_ECARD_PROMO_SETTINGS.fixedBonusAmount);

  const [notes, setNotes] = useState<string>("");

  // Tag selection in modal
  const [selectedTagIds, setSelectedTagIds] = useState<Set<string>>(new Set());

  // Create-new-tag inline state
  const [newTagOpen, setNewTagOpen] = useState(false);
  const [newTagName, setNewTagName] = useState("");
  const [newTagDesc, setNewTagDesc] = useState("");
  const [newTagColor, setNewTagColor] = useState(DEFAULT_ECARD_PROMO_SETTINGS.defaultTagColor);
  const [creatingTag, setCreatingTag] = useState(false);

  async function loadPromoDefaults() {
    const { data, error } = await supabase
      .from("settings")
      .select("name, value")
      .in("name", ECARD_PROMO_SETTING_NAMES);

    if (error) {
      console.error("Load eCard promo settings error:", error);
      toast({
        title: "eCard defaults not loaded",
        description: "Promotion defaults are using safe fallback values.",
      });
      return DEFAULT_ECARD_PROMO_SETTINGS;
    }

    const mapped = new Map((data ?? []).map((row) => [row.name, row.value]));

    const nextDefaults: EcardPromoDefaults = {
      minQualifyingAmount: mapped.get("ecard_promo_min_qualifying_amount") || DEFAULT_ECARD_PROMO_SETTINGS.minQualifyingAmount,
      belowMinBonusRate: mapped.get("ecard_promo_below_min_bonus_rate") || DEFAULT_ECARD_PROMO_SETTINGS.belowMinBonusRate,
      maxBonusBase: mapped.get("ecard_promo_max_bonus_base") || DEFAULT_ECARD_PROMO_SETTINGS.maxBonusBase,
      bonusType: normalizeBonusType(mapped.get("ecard_promo_bonus_type")),
      percentRate: mapped.get("ecard_promo_percent_rate") || DEFAULT_ECARD_PROMO_SETTINGS.percentRate,
      baseAmount: mapped.get("ecard_promo_base_amount") || DEFAULT_ECARD_PROMO_SETTINGS.baseAmount,
      bonusPerBase: mapped.get("ecard_promo_bonus_per_base") || DEFAULT_ECARD_PROMO_SETTINGS.bonusPerBase,
      fixedBonusAmount: mapped.get("ecard_promo_fixed_bonus_amount") || DEFAULT_ECARD_PROMO_SETTINGS.fixedBonusAmount,
      defaultTagColor: mapped.get("ecard_promo_default_tag_color") || DEFAULT_ECARD_PROMO_SETTINGS.defaultTagColor,
    };

    setPromoDefaults(nextDefaults);
    return nextDefaults;
  }

  async function loadPromos() {
    setLoading(true);
    try {
      const [pRes, tRes, ptRes] = await Promise.all([
        supabase.from("ecard_promotions").select("*").order("start_date", { ascending: false }),
        supabase.from("ecard_tags").select("*").eq("applies_to_promotion", true).order("name"),
        supabase.from("ecard_promo_tags").select("promo_id, tag_id"),
      ]);

      if (pRes.error) {
        toast({ title: "Load promos failed", description: pRes.error.message });
        console.error("Load promos error:", pRes.error);
        setPromos([]);
        return;
      }

      const data = Array.isArray(pRes.data) ? (pRes.data as Promo[]) : [];
      setPromos(data);
      setAllTags(Array.isArray(tRes.data) ? (tRes.data as EcardTag[]) : []);

      const map: Record<string, string[]> = {};
      for (const r of (ptRes.data ?? []) as Array<{ promo_id: string; tag_id: string }>) {
        if (!map[r.promo_id]) map[r.promo_id] = [];
        map[r.promo_id].push(r.tag_id);
      }
      setPromoTagMap(map);
    } catch (err) {
      console.error("Load promos exception:", err);
      toast({ title: "Load promos failed", description: String(err) });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadPromoDefaults();
    loadPromos();
  }, []);

  function toggleExpanded(id: string) {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  function resetForm(defaults = promoDefaults) {
    setPromoName("");
    setStartDate(isoDate(new Date()));
    setEndDate(isoDate(new Date()));
    setIsActive(true);
    setMinQual(defaults.minQualifyingAmount);
    setBelowMinRate(defaults.belowMinBonusRate);
    setMaxBonusBase(defaults.maxBonusBase);
    setBonusType(defaults.bonusType);
    setPercentRate(defaults.percentRate);
    setBaseAmount(defaults.baseAmount);
    setBonusPerBase(defaults.bonusPerBase);
    setFixedBonusAmount(defaults.fixedBonusAmount);
    setNotes("");
    setSelectedTagIds(new Set());
    setNewTagOpen(false);
    setNewTagName("");
    setNewTagDesc("");
    setNewTagColor(defaults.defaultTagColor);
  }

  async function openNewPromoModal() {
    setEditingId(null);
    const latestDefaults = await loadPromoDefaults();
    resetForm(latestDefaults);
    setModalOpen(true);
  }

  function openEditPromoModal(p: Promo) {
    setEditingId(p.id);
    setPromoName(p.promo_name);
    setStartDate(p.start_date);
    setEndDate(p.end_date);
    setIsActive(isPromoActive(p.is_active));
    setMinQual(String(p.min_qualifying_amount ?? promoDefaults.minQualifyingAmount));
    setBelowMinRate(String(p.below_min_bonus_rate ?? promoDefaults.belowMinBonusRate));
    setMaxBonusBase(String(p.max_bonus_base ?? promoDefaults.maxBonusBase));
    setBonusType(p.bonus_type);
    setPercentRate(String(p.percent_rate ?? 0));
    setBaseAmount(String(p.base_amount ?? promoDefaults.baseAmount));
    setBonusPerBase(String(p.bonus_per_base ?? 0));
    setFixedBonusAmount(String(p.fixed_bonus_amount ?? promoDefaults.fixedBonusAmount));
    setNotes(p.promo_notes ?? "");
    setSelectedTagIds(new Set(promoTagMap[p.id] ?? []));
    setNewTagOpen(false);
    setNewTagName("");
    setNewTagDesc("");
    setNewTagColor(promoDefaults.defaultTagColor);
    setModalOpen(true);
  }

  function validate(): { ok: boolean; msg?: string } {
    if (!promoName.trim()) return { ok: false, msg: "Promo name is required" };
    if (!startDate || !endDate) return { ok: false, msg: "Start and end dates are required" };
    if (endDate < startDate) return { ok: false, msg: "End date must be on/after start date" };

    const minQ = Number(minQual);
    const below = Number(belowMinRate);
    const cap = Number(maxBonusBase);

    if (!Number.isFinite(minQ) || minQ < 0) return { ok: false, msg: "Min qualifying must be >= 0" };
    if (!Number.isFinite(below) || below < 0) return { ok: false, msg: "Below-min rate must be >= 0" };
    if (!Number.isFinite(cap) || cap <= 0) return { ok: false, msg: "Max bonus base must be > 0" };

    if (bonusType === "PERCENT") {
      const pr = Number(percentRate);
      if (!Number.isFinite(pr) || pr < 0) return { ok: false, msg: "Percent rate must be >= 0 (e.g. 0.40)" };
      return { ok: true };
    }

    if (bonusType === "FIXED_AMOUNT") {
      const fb = Number(fixedBonusAmount);
      if (!Number.isFinite(fb) || fb < 0) return { ok: false, msg: "Fixed bonus amount must be >= 0" };
      return { ok: true };
    }

    const base = Number(baseAmount);
    const per = Number(bonusPerBase);
    if (!Number.isFinite(base) || base <= 0) return { ok: false, msg: "Base amount must be > 0" };
    if (!Number.isFinite(per) || per < 0) return { ok: false, msg: "Bonus per base must be >= 0" };
    return { ok: true };
  }

  async function createNewTagInline() {
    if (!newTagName.trim()) {
      toast({ title: "Tag name required" });
      return;
    }
    setCreatingTag(true);
    try {
      const ins = await supabase
        .from("ecard_tags")
        .insert({
          name: newTagName.trim(),
          description: newTagDesc.trim() || null,
          color: newTagColor,
          applies_to_card: false,
          applies_to_promotion: true,
          is_active: true,
        })
        .select("*")
        .single();
      if (ins.error) {
        toast({ title: "Create tag failed", description: ins.error.message });
        return;
      }
      const t = ins.data as EcardTag;
      // Append to local list and auto-select
      setAllTags((prev) => [...prev, t].sort((a, b) => a.name.localeCompare(b.name)));
      setSelectedTagIds((prev) => {
        const next = new Set(prev);
        next.add(t.id);
        return next;
      });
      toast({ title: "Tag created", description: `"${t.name}" added and selected` });
      setNewTagOpen(false);
      setNewTagName("");
      setNewTagDesc("");
      setNewTagColor(promoDefaults.defaultTagColor);
    } finally {
      setCreatingTag(false);
    }
  }

  async function syncPromoTags(promoId: string) {
    // Replace tag assignments for this promo with current selectedTagIds.
    // We only delete/insert rows for promo-applicable tags so we don't touch
    // tags we never showed to the user.
    const promoTagIds = allTags.map((t) => t.id);
    if (promoTagIds.length > 0) {
      const del = await supabase
        .from("ecard_promo_tags")
        .delete()
        .eq("promo_id", promoId)
        .in("tag_id", promoTagIds);
      if (del.error) throw del.error;
    }

    const toInsert = Array.from(selectedTagIds).map((tagId) => ({
      promo_id: promoId,
      tag_id: tagId,
    }));
    if (toInsert.length > 0) {
      const ins = await supabase.from("ecard_promo_tags").insert(toInsert);
      if (ins.error) throw ins.error;
    }
  }

  async function savePromo() {
    const v = validate();
    if (!v.ok) return toast({ title: "Fix required", description: v.msg });

    const payload: Record<string, unknown> = {
      promo_name: promoName.trim(),
      start_date: startDate,
      end_date: endDate,
      is_active: isActive,

      min_qualifying_amount: Number(minQual),
      below_min_bonus_rate: Number(belowMinRate),
      max_bonus_base: Number(maxBonusBase),

      bonus_type: bonusType,
      promo_notes: notes.trim() ? notes.trim() : null,
    };

    if (bonusType === "PERCENT") {
      payload.percent_rate = Number(percentRate);
      payload.base_amount = null;
      payload.bonus_per_base = null;
      payload.fixed_bonus_amount = null;
    } else if (bonusType === "FIXED_AMOUNT") {
      payload.percent_rate = null;
      payload.base_amount = null;
      payload.bonus_per_base = null;
      payload.fixed_bonus_amount = Number(fixedBonusAmount);
    } else {
      payload.percent_rate = null;
      payload.base_amount = Number(baseAmount);
      payload.bonus_per_base = Number(bonusPerBase);
      payload.fixed_bonus_amount = null;
    }

    try {
      let savedId: string;
      if (editingId === null) {
        const ins = await supabase.from("ecard_promotions").insert(payload).select("*").single();
        if (ins.error) {
          toast({ title: "Create failed", description: ins.error.message });
          return;
        }
        savedId = (ins.data as Promo).id;
      } else {
        const upd = await supabase.from("ecard_promotions").update(payload).eq("id", editingId);
        if (upd.error) {
          toast({ title: "Update failed", description: upd.error.message });
          return;
        }
        savedId = editingId;
      }

      // Sync tag assignments
      try {
        await syncPromoTags(savedId);
      } catch (e: any) {
        toast({ title: "Tags partially saved", description: e?.message ?? String(e) });
      }

      toast({ title: editingId === null ? "Promo created" : "Promo updated" });
      setModalOpen(false);
      await loadPromos();
    } catch (e: any) {
      toast({ title: "Save failed", description: e?.message ?? String(e) });
    }
  }

  async function toggleActive(p: Promo, e: React.MouseEvent) {
    e.stopPropagation();
    const currentlyActive = isPromoActive(p.is_active);
    const upd = await supabase
      .from("ecard_promotions")
      .update({ is_active: !currentlyActive })
      .eq("id", p.id);

    if (upd.error) {
      toast({ title: "Toggle failed", description: upd.error.message });
      return;
    }
    toast({ title: `Promo ${!currentlyActive ? "activated" : "deactivated"}` });
    await loadPromos();
  }

  function toggleTagSelected(tagId: string) {
    setSelectedTagIds((prev) => {
      const next = new Set(prev);
      if (next.has(tagId)) next.delete(tagId);
      else next.add(tagId);
      return next;
    });
  }

  const today = isoDate(new Date());

  return (
    <div className="space-y-4 p-4">
      <Card>
        <CardHeader>
          <CardTitle>Admin • eCard • Promotions</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          Create promos for date ranges, deactivate/reactivate, and edit dates for reuse.
          New promotions use your eCard defaults from <strong>public.settings</strong>: minimum qualifying amount {fmtMoney(Number(promoDefaults.minQualifyingAmount))}, max bonus base {fmtMoney(Number(promoDefaults.maxBonusBase))}, and default bonus type {promoDefaults.bonusType}.
          Use <strong>Fixed Amount</strong> bonus type to give a flat dollar bonus on every qualifying top-up.
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Promo List</CardTitle>
          <div className="text-sm text-muted-foreground">
            {loading ? "Loading..." : `${promos.length} promo(s) found`}
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2">
            <Button onClick={openNewPromoModal}>
              + New Promo
            </Button>
            <Button variant="outline" onClick={() => loadPromos()} disabled={loading}>
              {loading ? "Loading..." : "Refresh"}
            </Button>
          </div>

          <ScrollArea className="h-[500px]">
            <div className="space-y-2">
              {promos.length === 0 && !loading && (
                <div className="text-sm text-muted-foreground text-center py-8 border rounded-md">
                  No promos found. Click "+ New Promo" to create one.
                </div>
              )}
              {loading && promos.length === 0 && (
                <div className="text-sm text-muted-foreground text-center py-8 border rounded-md">
                  Loading promotions...
                </div>
              )}

              {promos.map((p) => {
                const active = isPromoActive(p.is_active);
                const inRange = p.start_date <= today && p.end_date >= today;
                const isExpanded = expandedIds.has(p.id);
                const promoTagIds = promoTagMap[p.id] ?? [];
                const promoTags = promoTagIds
                  .map((id) => allTags.find((t) => t.id === id))
                  .filter(Boolean) as EcardTag[];

                return (
                  <Collapsible
                    key={p.id}
                    open={isExpanded}
                    onOpenChange={() => toggleExpanded(p.id)}
                  >
                    <div className="border rounded-md overflow-hidden">
                      {/* Main Row - Always Visible */}
                      <CollapsibleTrigger asChild>
                        <div className="flex items-center justify-between p-3 hover:bg-muted/50 cursor-pointer">
                          <div className="flex items-center gap-4 flex-1 min-w-0">
                            {/* Expand Icon */}
                            <div className="flex-shrink-0">
                              {isExpanded ? (
                                <ChevronDown className="h-4 w-4 text-muted-foreground" />
                              ) : (
                                <ChevronRight className="h-4 w-4 text-muted-foreground" />
                              )}
                            </div>

                            {/* Name */}
                            <div className="flex-1 min-w-0">
                              <div className="font-medium truncate">{p.promo_name}</div>
                              <div className="flex flex-wrap items-center gap-1 mt-0.5">
                                {inRange && active && (
                                  <span className="text-xs text-green-500">Active today</span>
                                )}
                                {promoTags.slice(0, 4).map((t) => (
                                  <Badge
                                    key={t.id}
                                    style={{ backgroundColor: t.color, color: "#fff" }}
                                    className="text-xs"
                                  >
                                    {t.name}
                                  </Badge>
                                ))}
                                {promoTags.length > 4 && (
                                  <Badge variant="outline" className="text-xs">+{promoTags.length - 4}</Badge>
                                )}
                              </div>
                            </div>

                            {/* Dates */}
                            <div className="flex-shrink-0 text-sm text-muted-foreground whitespace-nowrap hidden sm:block">
                              {formatDate(p.start_date)} – {formatDate(p.end_date)}
                            </div>

                            {/* Status Badge */}
                            <div className="flex-shrink-0">
                              {active ? (
                                <Badge className="bg-green-600">Active</Badge>
                              ) : (
                                <Badge variant="secondary">Inactive</Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      </CollapsibleTrigger>

                      {/* Expanded Details */}
                      <CollapsibleContent>
                        <div className="border-t bg-muted/30 p-4 space-y-4">
                          {/* Dates for mobile */}
                          <div className="sm:hidden">
                            <div className="text-xs text-muted-foreground mb-1">Date Range</div>
                            <div className="text-sm">{formatDate(p.start_date)} – {formatDate(p.end_date)}</div>
                          </div>

                          {/* Bonus Configuration */}
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div>
                              <div className="text-xs text-muted-foreground mb-1">Bonus Type</div>
                              <div className="text-sm font-medium">{p.bonus_type}</div>
                            </div>
                            <div>
                              <div className="text-xs text-muted-foreground mb-1">Rule</div>
                              <div className="text-sm font-medium">{formatPromoSummary(p)}</div>
                            </div>
                            <div>
                              <div className="text-xs text-muted-foreground mb-1">Min Qualifying</div>
                              <div className="text-sm font-medium">{fmtMoney(p.min_qualifying_amount)}</div>
                            </div>
                            <div>
                              <div className="text-xs text-muted-foreground mb-1">Max Bonus Base</div>
                              <div className="text-sm font-medium">{fmtMoney(p.max_bonus_base)}</div>
                            </div>
                          </div>

                          {/* Rate Details */}
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div>
                              <div className="text-xs text-muted-foreground mb-1">Below-Min Rate</div>
                              <div className="text-sm font-medium">{fmtRate(p.below_min_bonus_rate)}</div>
                            </div>
                            {p.bonus_type === "PERCENT" && (
                              <div>
                                <div className="text-xs text-muted-foreground mb-1">Percent Rate</div>
                                <div className="text-sm font-medium">{fmtPercent(p.percent_rate)}</div>
                              </div>
                            )}
                            {p.bonus_type === "FIXED_AMOUNT" && (
                              <div>
                                <div className="text-xs text-muted-foreground mb-1">Fixed Bonus</div>
                                <div className="text-sm font-medium">{fmtMoney(p.fixed_bonus_amount)}</div>
                              </div>
                            )}
                            {p.bonus_type === "PER_BASE_AMOUNT" && (
                              <>
                                <div>
                                  <div className="text-xs text-muted-foreground mb-1">Base Amount</div>
                                  <div className="text-sm font-medium">{fmtMoney(p.base_amount)}</div>
                                </div>
                                <div>
                                  <div className="text-xs text-muted-foreground mb-1">Bonus Per Base</div>
                                  <div className="text-sm font-medium">{fmtMoney(p.bonus_per_base)}</div>
                                </div>
                              </>
                            )}
                          </div>

                          {/* Tags */}
                          <div>
                            <div className="text-xs text-muted-foreground mb-1">Tags ({promoTags.length})</div>
                            {promoTags.length === 0 ? (
                              <div className="text-sm text-muted-foreground">No tags</div>
                            ) : (
                              <div className="flex flex-wrap gap-1">
                                {promoTags.map((t) => (
                                  <Badge
                                    key={t.id}
                                    style={{ backgroundColor: t.color, color: "#fff" }}
                                    className="text-xs"
                                  >
                                    {t.name}
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </div>

                          {/* Notes */}
                          {p.promo_notes && (
                            <div>
                              <div className="text-xs text-muted-foreground mb-1">Notes</div>
                              <div className="text-sm">{p.promo_notes}</div>
                            </div>
                          )}

                          {/* Timestamps */}
                          <div className="grid grid-cols-2 gap-4 text-xs text-muted-foreground">
                            <div>
                              <span className="font-medium">Created:</span> {formatDateTime(p.created_at)}
                            </div>
                            <div>
                              <span className="font-medium">Updated:</span> {formatDateTime(p.updated_at)}
                            </div>
                          </div>

                          {/* Action Buttons */}
                          <div className="flex gap-2 pt-2 border-t">
                            <Button
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                openEditPromoModal(p);
                              }}
                            >
                              Edit Promo
                            </Button>
                            <Button
                              size="sm"
                              variant={active ? "destructive" : "outline"}
                              onClick={(e) => toggleActive(p, e)}
                            >
                              {active ? "Deactivate" : "Activate"}
                            </Button>
                          </div>
                        </div>
                      </CollapsibleContent>
                    </div>
                  </Collapsible>
                );
              })}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Create/Edit Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId === null ? "Create New Promo" : "Edit Promo"}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <div className="text-xs text-muted-foreground mb-1">Promo Name *</div>
                <Input
                  value={promoName}
                  onChange={(e) => setPromoName(e.target.value)}
                  placeholder="e.g. Dec Double, Summer Special"
                />
              </div>

              <div>
                <div className="text-xs text-muted-foreground mb-1">Status</div>
                <Select value={isActive ? "yes" : "no"} onValueChange={(v) => setIsActive(v === "yes")}>
                  <SelectTrigger>
                    <SelectValue placeholder="Active?" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">Active</SelectItem>
                    <SelectItem value="no">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <div className="text-xs text-muted-foreground mb-1">Start Date *</div>
                <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              </div>

              <div>
                <div className="text-xs text-muted-foreground mb-1">End Date *</div>
                <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
              </div>
            </div>

            <div className="border-t pt-4">
              <div className="text-sm font-medium mb-2">Bonus Configuration</div>
              <div className="grid gap-4 md:grid-cols-3">
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Min Qualifying ($)</div>
                  <Input value={minQual} onChange={(e) => setMinQual(e.target.value)} placeholder={promoDefaults.minQualifyingAmount} />
                </div>
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Below-Min Rate</div>
                  <Input value={belowMinRate} onChange={(e) => setBelowMinRate(e.target.value)} placeholder={promoDefaults.belowMinBonusRate} />
                </div>
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Max Bonus Base ($)</div>
                  <Input value={maxBonusBase} onChange={(e) => setMaxBonusBase(e.target.value)} placeholder={promoDefaults.maxBonusBase} />
                </div>
              </div>
            </div>

            <div className="border-t pt-4">
              <div className="text-sm font-medium mb-2">Bonus Type & Rate</div>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Bonus Type</div>
                  <Select value={bonusType} onValueChange={(v) => setBonusType(v as BonusType)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Bonus type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PERCENT">Percent bonus (e.g. 0.40 = 40%)</SelectItem>
                      <SelectItem value="PER_BASE_AMOUNT">$ per base amount (e.g. $5 per $25)</SelectItem>
                      <SelectItem value="FIXED_AMOUNT">Fixed bonus amount (e.g. $10 flat)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {bonusType === "PERCENT" && (
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">Percent Rate (e.g. 0.40 = 40%, 1.00 = 100%)</div>
                    <Input value={percentRate} onChange={(e) => setPercentRate(e.target.value)} placeholder={promoDefaults.percentRate} />
                  </div>
                )}
                {bonusType === "FIXED_AMOUNT" && (
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">Fixed Bonus Amount ($)</div>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={fixedBonusAmount}
                      onChange={(e) => setFixedBonusAmount(e.target.value)}
                      placeholder={promoDefaults.fixedBonusAmount}
                    />
                    <div className="text-xs text-muted-foreground mt-1">
                      Flat $ bonus on every top-up that meets the min qualifying amount.
                    </div>
                  </div>
                )}
                {bonusType === "PER_BASE_AMOUNT" && (
                  <>
                    <div>
                      <div className="text-xs text-muted-foreground mb-1">Base Amount ($)</div>
                      <Input value={baseAmount} onChange={(e) => setBaseAmount(e.target.value)} placeholder={promoDefaults.baseAmount} />
                    </div>
                    <div className="md:col-span-2">
                      <div className="text-xs text-muted-foreground mb-1">Bonus Per Base ($)</div>
                      <Input value={bonusPerBase} onChange={(e) => setBonusPerBase(e.target.value)} placeholder={promoDefaults.bonusPerBase} />
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Tags Section */}
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-medium flex items-center gap-2">
                  <TagIcon className="h-4 w-4" />
                  Tags ({selectedTagIds.size} selected)
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setNewTagOpen((v) => !v)}
                >
                  {newTagOpen ? (
                    <>
                      <X className="h-3 w-3 mr-1" />
                      Cancel new tag
                    </>
                  ) : (
                    <>
                      <Plus className="h-3 w-3 mr-1" />
                      Create new tag
                    </>
                  )}
                </Button>
              </div>

              {newTagOpen && (
                <div className="border rounded-md p-3 mb-3 bg-muted/30 space-y-2">
                  <div className="grid gap-2 md:grid-cols-2">
                    <div>
                      <Label className="text-xs">Tag Name *</Label>
                      <Input
                        value={newTagName}
                        onChange={(e) => setNewTagName(e.target.value)}
                        placeholder="e.g. Dec Double, Driver Bonus"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Description</Label>
                      <Input
                        value={newTagDesc}
                        onChange={(e) => setNewTagDesc(e.target.value)}
                        placeholder="Optional"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Color</Label>
                    <div className="flex flex-wrap gap-2 mt-1 items-center">
                      {COLOR_PRESETS.map((c) => (
                        <button
                          key={c}
                          type="button"
                          className={`w-7 h-7 rounded-full border-2 ${
                            newTagColor === c ? "border-foreground ring-2 ring-foreground/30" : "border-transparent"
                          }`}
                          style={{ backgroundColor: c }}
                          onClick={() => setNewTagColor(c)}
                        />
                      ))}
                      <Input
                        type="color"
                        value={newTagColor}
                        onChange={(e) => setNewTagColor(e.target.value)}
                        className="w-12 h-8 p-1"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <Button
                      type="button"
                      size="sm"
                      onClick={createNewTagInline}
                      disabled={creatingTag || !newTagName.trim()}
                    >
                      {creatingTag ? "Creating..." : "Create & Select Tag"}
                    </Button>
                  </div>
                </div>
              )}

              {allTags.length === 0 ? (
                <div className="text-xs text-muted-foreground border rounded-md p-3 bg-muted/30">
                  No promo-applicable tags exist yet. Click "Create new tag" above, or go to the Tags tab to manage tags.
                </div>
              ) : (
                <ScrollArea className="h-[160px] border rounded-md p-2">
                  <div className="space-y-1">
                    {allTags.map((t) => {
                      const checked = selectedTagIds.has(t.id);
                      return (
                        <label
                          key={t.id}
                          className="flex items-center gap-2 p-1 hover:bg-muted/50 rounded cursor-pointer"
                        >
                          <Checkbox
                            checked={checked}
                            onCheckedChange={() => toggleTagSelected(t.id)}
                          />
                          <Badge
                            style={{ backgroundColor: t.color, color: "#fff" }}
                            className="text-xs"
                          >
                            {t.name}
                          </Badge>
                          {t.description && (
                            <span className="text-xs text-muted-foreground truncate">
                              {t.description}
                            </span>
                          )}
                          {!t.is_active && (
                            <Badge variant="secondary" className="text-xs">Inactive</Badge>
                          )}
                        </label>
                      );
                    })}
                  </div>
                </ScrollArea>
              )}
            </div>

            <div className="border-t pt-4">
              <div className="text-xs text-muted-foreground mb-1">Notes (optional)</div>
              <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Any additional notes..." />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={savePromo}>
              {editingId === null ? "Create Promo" : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
